// src/components/PhoneSigninModal.js
//
// SMS-code sign-in for passwordless accounts created via the anon
// booking flow. Opens from a link on /login. Two phases mirroring
// AnonVerifyModal, but for existing users:
//
//   collect  — Phone. POST /api/auth/request-phone-signin
//   verify   — 6-digit code. POST /api/auth/verify-phone-signin
//              → session established → redirect to /
//
// Reuses the same visual chrome as AnonVerifyModal so the two flows
// look like siblings.

import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { X, Loader2, ArrowRight, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthContext';

function formatPhoneUS(raw) {
  const digits = String(raw || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export default function PhoneSigninModal({ open, onClose }) {
  const navigate = useNavigate();
  const { setUser } = useContext(AuthContext);
  const [phase, setPhase] = useState('collect');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [verificationId, setVerificationId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const phoneRef = useRef(null);
  const codeRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPhase('collect'); setPhone(''); setCode('');
      setVerificationId(null); setErr('');
      setTimeout(() => phoneRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (phase === 'verify') setTimeout(() => codeRef.current?.focus(), 50);
  }, [phase]);

  const requestCode = useCallback(async () => {
    setErr('');
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) { setErr('Please enter a 10-digit US phone number.'); return; }
    setBusy(true);
    try {
      const res = await fetch('/api/auth/request-phone-signin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ phone: digits }),
      });
      const data = await res.json();
      if (!res.ok) {
        setErr(data?.message || `Couldn't send code (${res.status}).`);
        return;
      }
      setVerificationId(data.verificationId);
      setPhase('verify');
    } catch (e) {
      setErr(e?.message || 'Network error');
    } finally { setBusy(false); }
  }, [phone]);

  const verifyCode = useCallback(async () => {
    setErr('');
    const c = code.replace(/\D/g, '').slice(0, 6);
    if (c.length !== 6) { setErr('Enter the 6-digit code we texted you.'); return; }
    setBusy(true);
    try {
      const res = await fetch('/api/auth/verify-phone-signin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ verificationId, code: c }),
      });
      const data = await res.json();
      if (!res.ok) {
        setErr(data?.message || `Verification failed (${res.status}).`);
        return;
      }
      // Hydrate AuthContext so HomeGate immediately redirects to
      // /p/:joinCode instead of routing via /login again. If the
      // response carries upgraded: true, the server promoted a
      // managed-client record on the visitor's behalf — worth
      // acknowledging in a follow-up polish pass with a toast.
      if (typeof setUser === 'function' && data?.user) setUser(data.user);
      onClose();
      navigate('/');
    } catch (e) {
      setErr(e?.message || 'Network error');
    } finally { setBusy(false); }
  }, [code, verificationId, onClose, navigate, setUser]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center"
      role="dialog" aria-modal="true" aria-label="Sign in with phone"
    >
      <div
        onClick={onClose}
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(20, 18, 14, 0.42)' }}
      />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-md mx-auto"
        style={{
          maxWidth: 440,
          backgroundColor: 'var(--bg-elev)',
          borderRadius: 22,
          boxShadow: 'var(--shadow-sheet)',
          maxHeight: '90vh',
          overflowY: 'auto',
          paddingBottom: 'env(safe-area-inset-bottom, 12px)',
        }}
      >
        <button
          type="button" onClick={onClose} aria-label="Close"
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-paper-deep"
          style={{ color: 'var(--ink-3)' }}
        >
          <X className="w-5 h-5" />
        </button>
        <header className="px-5 pt-5 pb-3">
          <h2
            className="font-display text-ink"
            style={{ fontSize: 22, fontWeight: 500, letterSpacing: '-0.005em' }}
          >
            {phase === 'collect' ? 'Sign in with your phone' : 'Enter the code'}
          </h2>
          <p className="text-sm text-ink-2 mt-1.5 leading-snug">
            {phase === 'collect'
              ? 'We\'ll text you a 6-digit code to sign in. Works for accounts created at a provider\'s booking page.'
              : <>Texted to <strong className="text-ink">{phone}</strong>.</>}
          </p>
        </header>
        <div className="px-5 pb-5 space-y-4">
          {phase === 'collect' ? (
            <>
              <label className="block">
                <span className="block text-xs text-ink-2 mb-1">Mobile number</span>
                <input
                  ref={phoneRef}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  value={phone}
                  onChange={(e) => setPhone(formatPhoneUS(e.target.value))}
                  placeholder="(555) 123-4567"
                  className="w-full"
                  style={{
                    minHeight: 44, padding: '10px 14px', borderRadius: 10,
                    border: '1px solid var(--line)', backgroundColor: 'var(--bg)',
                    color: 'var(--ink)', fontSize: 15,
                  }}
                />
              </label>
              {err && <p className="text-xs" style={{ color: 'var(--danger)' }}>{err}</p>}
              <button
                type="button" onClick={requestCode} disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2"
                style={{
                  minHeight: 48, borderRadius: 999,
                  backgroundColor: 'var(--cta-bg)', color: 'var(--cta-fg)',
                  fontFamily: 'var(--font-sans)', fontSize: 14, fontWeight: 600,
                  border: 'none', opacity: busy ? 0.6 : 1,
                }}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {busy ? 'Sending…' : 'Text me a code'}
              </button>
            </>
          ) : (
            <>
              <label className="block">
                <span className="block text-xs text-ink-2 mb-1">6-digit code</span>
                <input
                  ref={codeRef} type="text" inputMode="numeric" autoComplete="one-time-code"
                  maxLength={6} value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="••••••"
                  className="w-full text-center"
                  style={{
                    minHeight: 56, padding: '10px 14px', borderRadius: 10,
                    border: '1px solid var(--line)', backgroundColor: 'var(--bg)',
                    color: 'var(--ink)', fontSize: 22, letterSpacing: '0.5em',
                    fontFamily: 'var(--font-mono)',
                  }}
                />
              </label>
              {err && <p className="text-xs" style={{ color: 'var(--danger)' }}>{err}</p>}
              <button
                type="button" onClick={verifyCode} disabled={busy || code.length < 6}
                className="w-full inline-flex items-center justify-center gap-2"
                style={{
                  minHeight: 48, borderRadius: 999,
                  backgroundColor: 'var(--cta-bg)', color: 'var(--cta-fg)',
                  fontFamily: 'var(--font-sans)', fontSize: 14, fontWeight: 600,
                  border: 'none', opacity: (busy || code.length < 6) ? 0.5 : 1,
                }}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {busy ? 'Verifying…' : 'Sign in'}
              </button>
              <button
                type="button"
                onClick={() => { setPhase('collect'); setCode(''); setErr(''); }}
                className="w-full text-center text-xs underline"
                style={{ color: 'var(--ink-3)', background: 'none', border: 'none', padding: '6px 0', cursor: 'pointer' }}
              >
                Use a different number
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
